import { useMutation, useQuery } from "@tanstack/react-query";

import { axiosInstance, NOTIFICATIONS } from "api";

export type UserNotificationPreferenceResponse = {
  priceChanges: boolean;
};

export type PlantNotificationPreferenceResponse = {
  powerPlantMeterId: string;
  powerPlantName: string;
  // Plant sells through Tradea and shows market prices: the two advice
  // toggles below apply only then.
  tradeaAdvice: boolean;
  statusChanges: boolean;
  dailySummary: boolean;
  curtailmentAdvice: boolean;
  settlementAdvice: boolean;
};

export const useGetVapidKey = () => {
  return useQuery<{ publicKey: string }>({
    queryKey: ["vapidKey"],
    queryFn: async () => {
      const { data } = await axiosInstance.get<{ publicKey: string }>(
        NOTIFICATIONS.VAPID_KEY,
      );
      return data;
    },
    staleTime: Infinity,
  });
};

export const useSubscribePush = () => {
  return useMutation({
    mutationFn: (subscription: PushSubscriptionJSON) =>
      axiosInstance.post(NOTIFICATIONS.SUBSCRIBE, { subscription }),
  });
};

export const useUnsubscribePush = () => {
  return useMutation({
    mutationFn: (endpoint: string) =>
      axiosInstance.delete(NOTIFICATIONS.SUBSCRIBE, { data: { endpoint } }),
  });
};

export const useGetUserNotificationPreferences = () => {
  return useQuery<UserNotificationPreferenceResponse>({
    queryKey: ["userNotificationPreferences"],
    queryFn: async () => {
      const { data } = await axiosInstance.get<UserNotificationPreferenceResponse>(
        NOTIFICATIONS.USER_PREFERENCES,
      );
      return data;
    },
  });
};

export const useUpdateUserNotificationPreference = () => {
  return useMutation({
    mutationFn: (payload: Partial<UserNotificationPreferenceResponse>) =>
      axiosInstance.put(NOTIFICATIONS.USER_PREFERENCES, payload),
  });
};

export const useGetPlantNotificationPreferences = () => {
  return useQuery<PlantNotificationPreferenceResponse[]>({
    queryKey: ["plantNotificationPreferences"],
    queryFn: async () => {
      const { data } = await axiosInstance.get<PlantNotificationPreferenceResponse[]>(
        NOTIFICATIONS.PLANT_PREFERENCES,
      );
      return data;
    },
  });
};

export const useUpdatePlantNotificationPreference = () => {
  return useMutation({
    mutationFn: ({
      meterId,
      ...payload
    }: {
      meterId: string;
      statusChanges?: boolean;
      dailySummary?: boolean;
      curtailmentAdvice?: boolean;
      settlementAdvice?: boolean;
    }) => axiosInstance.put(NOTIFICATIONS.PLANT_PREFERENCE(meterId), payload),
  });
};
